import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Truck,
  Building2,
  ChevronDown,
  ChevronUp,
  DollarSign,
  X,
  Save,
  RefreshCw,
  Globe,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminZonasEnvio() {
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, totalCiudades: 0, zonasActivas: 0 });
  const [expandedZona, setExpandedZona] = useState(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingZona, setEditingZona] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    costo_domicilio: "",
    orden: 99,
    activo: true,
    ciudades: []
  });
  
  // New city form
  const [newCity, setNewCity] = useState({ ciudad: "", departamento: "" });

  const fetchZonas = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/zonas-envio/admin/zonas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setZonas(data.zonas || []);
        setStats({
          total: data.total || 0,
          totalCiudades: data.total_ciudades || 0,
          zonasActivas: data.zonas_activas || 0
        });
      } else {
        toast.error(data.detail || "Error al cargar zonas");
      }
    } catch (err) {
      console.error("Error fetching zonas:", err);
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchZonas();
  }, [fetchZonas]);

  const handleSeedZonas = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/zonas-envio/admin/seed-zonas`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchZonas();
      } else {
        toast.error(data.detail || "Error al crear zonas iniciales");
      }
    } catch (err) {
      toast.error("Error de conexión");
    }
  };

  const handleOpenModal = (zona = null) => {
    if (zona) {
      setEditingZona(zona);
      setFormData({
        nombre: zona.nombre,
        descripcion: zona.descripcion || "",
        costo_domicilio: zona.costo_domicilio.toString(),
        orden: zona.orden || 99,
        activo: zona.activo !== false,
        ciudades: zona.ciudades || []
      });
    } else {
      setEditingZona(null);
      setFormData({
        nombre: "",
        descripcion: "",
        costo_domicilio: "",
        orden: 99,
        activo: true,
        ciudades: []
      });
    }
    setNewCity({ ciudad: "", departamento: "" });
    setShowModal(true);
  };

  const handleAddCity = () => {
    if (!newCity.ciudad.trim() || !newCity.departamento.trim()) {
      toast.error("Ingresa ciudad y departamento");
      return;
    }
    
    // Check if city already exists
    const exists = formData.ciudades.some(
      c => c.ciudad.toLowerCase() === newCity.ciudad.toLowerCase()
    );
    if (exists) {
      toast.error("Esta ciudad ya está en la lista");
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      ciudades: [...prev.ciudades, { ...newCity, activo: true }]
    }));
    setNewCity({ ciudad: "", departamento: "" });
  };

  const handleRemoveCity = (index) => {
    setFormData(prev => ({
      ...prev,
      ciudades: prev.ciudades.filter((_, i) => i !== index)
    }));
  };

  const handleToggleCityActive = (index) => {
    setFormData(prev => ({
      ...prev,
      ciudades: prev.ciudades.map((c, i) => 
        i === index ? { ...c, activo: !c.activo } : c
      )
    }));
  };

  const handleSaveZona = async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!formData.costo_domicilio || parseInt(formData.costo_domicilio) < 0) {
      toast.error("El costo de domicilio es obligatorio");
      return;
    }
    if (formData.ciudades.length === 0) {
      toast.error("Agrega al menos una ciudad");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const url = editingZona 
        ? `${API_URL}/api/zonas-envio/admin/zonas/${editingZona.id}`
        : `${API_URL}/api/zonas-envio/admin/zonas`;
      
      const res = await fetch(url, {
        method: editingZona ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          nombre: formData.nombre,
          descripcion: formData.descripcion,
          costo_domicilio: parseInt(formData.costo_domicilio),
          orden: parseInt(formData.orden) || 99,
          activo: formData.activo,
          ciudades: formData.ciudades
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(editingZona ? "Zona actualizada" : "Zona creada");
        setShowModal(false);
        fetchZonas();
      } else {
        toast.error(data.detail || "Error al guardar");
      }
    } catch (err) {
      toast.error("Error de conexión");
    }
  };

  const handleToggleZona = async (zonaId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/zonas-envio/admin/zonas/${zonaId}/toggle`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchZonas();
      } else {
        toast.error(data.detail || "Error al cambiar estado");
      }
    } catch (err) {
      toast.error("Error de conexión");
    }
  };

  const handleDeleteZona = async (zonaId) => {
    if (!window.confirm("¿Eliminar esta zona de envío? Esta acción no se puede deshacer.")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/zonas-envio/admin/zonas/${zonaId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Zona eliminada");
        fetchZonas();
      } else {
        toast.error(data.detail || "Error al eliminar");
      }
    } catch (err) {
      toast.error("Error de conexión");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-zonas-envio">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck className="w-7 h-7 text-amber-600" />
            Zonas de Envío
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Configura las ciudades con cobertura y sus tarifas de domicilio
          </p>
        </div>
        <div className="flex gap-2">
          {zonas.length === 0 && (
            <Button 
              onClick={handleSeedZonas} 
              variant="outline"
              className="border-amber-600 text-amber-600 hover:bg-amber-50"
              data-testid="seed-zonas-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Cargar Zonas Iniciales
            </Button>
          )}
          <Button 
            onClick={() => handleOpenModal()} 
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="new-zona-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Zona
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Zonas</p>
              <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ciudades con Cobertura</p>
              <p className="text-2xl font-bold text-gray-800">{stats.totalCiudades}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Zonas Activas</p>
              <p className="text-2xl font-bold text-gray-800">{stats.zonasActivas}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Zonas List */}
      {zonas.length === 0 ? (
        <div className="bg-white rounded-xl p-8 border border-gray-200 text-center">
          <Truck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No hay zonas configuradas</h3>
          <p className="text-gray-400 mb-4">
            Carga las zonas iniciales o crea una nueva zona de envío
          </p>
          <Button onClick={handleSeedZonas} className="bg-amber-600 hover:bg-amber-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Cargar Zonas Iniciales
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {zonas.map((zona) => (
            <div
              key={zona.id}
              className={`bg-white rounded-xl border ${zona.activo ? 'border-gray-200' : 'border-red-200 bg-red-50/50'} shadow-sm overflow-hidden`}
              data-testid={`zona-card-${zona.id}`}
            >
              {/* Zona Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedZona(expandedZona === zona.id ? null : zona.id)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    zona.activo ? 'bg-amber-100' : 'bg-red-100'
                  }`}>
                    <MapPin className={`w-5 h-5 ${zona.activo ? 'text-amber-600' : 'text-red-500'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-800">{zona.nombre}</h3>
                      {!zona.activo && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                          Inactiva
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {zona.ciudades?.length || 0} ciudades · Orden: {zona.orden}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-amber-600">
                      ${zona.costo_domicilio?.toLocaleString("es-CO")}
                    </p>
                    <p className="text-xs text-gray-400">Domicilio</p>
                  </div>
                  {expandedZona === zona.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Expanded Content */}
              {expandedZona === zona.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                  {zona.descripcion && (
                    <p className="text-sm text-gray-600 mb-3">{zona.descripcion}</p>
                  )}
                  
                  {/* Cities Grid */}
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Ciudades en esta zona:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {zona.ciudades?.map((ciudad, idx) => (
                        <span
                          key={idx}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                            ciudad.activo !== false
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-gray-200 text-gray-500 line-through'
                          }`}
                        >
                          <Building2 className="w-3 h-3" />
                          {ciudad.ciudad}
                          <span className="text-xs opacity-70">({ciudad.departamento})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={zona.activo !== false}
                        onCheckedChange={() => handleToggleZona(zona.id)}
                      />
                      <span className="text-sm text-gray-600">
                        {zona.activo ? "Zona activa" : "Zona inactiva"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(zona);
                        }}
                        data-testid={`edit-zona-${zona.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteZona(zona.id);
                        }}
                        data-testid={`delete-zona-${zona.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-600" />
              {editingZona ? "Editar Zona de Envío" : "Nueva Zona de Envío"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nombre de la Zona *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: Zona 1 - Bogotá"
                  data-testid="zona-nombre-input"
                />
              </div>
              <div>
                <Label>Costo de Domicilio *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    value={formData.costo_domicilio}
                    onChange={(e) => setFormData(prev => ({ ...prev, costo_domicilio: e.target.value }))}
                    placeholder="7000"
                    className="pl-9"
                    data-testid="zona-costo-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Descripción (opcional)</Label>
                <Input
                  value={formData.descripcion}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripción de la zona..."
                />
              </div>
              <div>
                <Label>Orden de aparición</Label>
                <Input
                  type="number"
                  value={formData.orden}
                  onChange={(e) => setFormData(prev => ({ ...prev, orden: e.target.value }))}
                  placeholder="1"
                  min="1"
                  max="99"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.activo}
                onCheckedChange={(v) => setFormData(prev => ({ ...prev, activo: v }))}
              />
              <Label>Zona activa</Label>
            </div>
            
            {/* Cities Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold mb-3 block">Ciudades en esta Zona</Label>
              
              {/* Add City Form */}
              <div className="flex gap-2 mb-4">
                <Input
                  value={newCity.ciudad}
                  onChange={(e) => setNewCity(prev => ({ ...prev, ciudad: e.target.value }))}
                  placeholder="Ciudad"
                  className="flex-1"
                  data-testid="new-city-input"
                />
                <Input
                  value={newCity.departamento}
                  onChange={(e) => setNewCity(prev => ({ ...prev, departamento: e.target.value }))}
                  placeholder="Departamento"
                  className="flex-1"
                  data-testid="new-dept-input"
                />
                <Button
                  type="button"
                  onClick={handleAddCity}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="add-city-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Cities List */}
              {formData.ciudades.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  No hay ciudades agregadas. Agrega al menos una ciudad.
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.ciudades.map((ciudad, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        ciudad.activo !== false ? 'bg-amber-50' : 'bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className={`w-4 h-4 ${ciudad.activo !== false ? 'text-amber-600' : 'text-gray-400'}`} />
                        <span className={ciudad.activo !== false ? '' : 'line-through text-gray-400'}>
                          {ciudad.ciudad}
                        </span>
                        <span className="text-xs text-gray-500">({ciudad.departamento})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleCityActive(idx)}
                          className="h-7 w-7 p-0"
                        >
                          <Switch checked={ciudad.activo !== false} className="scale-75" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCity(idx)}
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveZona} 
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-zona-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingZona ? "Guardar Cambios" : "Crear Zona"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
